/**
 * Pointy-top axial hex lattice covering the map rectangle. Shared by the
 * generator (tools/mapgen) and the decoder so both derive identical geometry.
 *
 * Cells are stored row-major: cell = row * cols + col. Odd rows are shifted
 * right by half a hex ("odd-r" offset). Axial coordinates: r = row,
 * q = col − floor(row / 2). Centre: x = originX + √3·R·(q + r/2), y = originY + 1.5·R·r.
 * Only +, ×, / and Math.sqrt are used, so centres are bit-identical everywhere.
 */

export const SQRT3 = Math.sqrt(3);

/** Axial neighbour directions in slot order: E, NE, NW, W, SW, SE (y grows southwards). */
export const HEX_DIRS: readonly (readonly [number, number])[] = [
  [1, 0],
  [1, -1],
  [0, -1],
  [-1, 0],
  [-1, 1],
  [0, 1],
];

export interface Lattice {
  hexR: number;
  rows: number;
  cols: number;
  originX: number;
  originY: number;
}

export function makeLattice(hexR: number, width: number, height: number): Lattice {
  const w = SQRT3 * hexR;
  const originX = w / 2;
  const originY = hexR / 2;
  const rows = Math.ceil((height - hexR) / (1.5 * hexR)) + 1;
  const cols = Math.ceil(width / w);
  return { hexR, rows, cols, originX, originY };
}

export function cellQ(lat: Lattice, cell: number): number {
  const row = Math.floor(cell / lat.cols);
  return (cell - row * lat.cols) - Math.floor(row / 2);
}

export function cellR(lat: Lattice, cell: number): number {
  return Math.floor(cell / lat.cols);
}

export function axialToCell(lat: Lattice, q: number, r: number): number {
  if (r < 0 || r >= lat.rows) return -1;
  const col = q + Math.floor(r / 2);
  if (col < 0 || col >= lat.cols) return -1;
  return r * lat.cols + col;
}

export function axialCenterX(lat: Lattice, q: number, r: number): number {
  return lat.originX + SQRT3 * lat.hexR * (q + r / 2);
}

export function axialCenterY(lat: Lattice, r: number): number {
  return lat.originY + 1.5 * lat.hexR * r;
}

/** Fractional axial → nearest axial (cube rounding). Returns [q, r]. */
export function axialRound(qf: number, rf: number): [number, number] {
  const sf = -qf - rf;
  let q = Math.round(qf);
  let r = Math.round(rf);
  const s = Math.round(sf);
  const dq = Math.abs(q - qf);
  const dr = Math.abs(r - rf);
  const ds = Math.abs(s - sf);
  if (dq > dr && dq > ds) q = -r - s;
  else if (dr > ds) r = -q - s;
  return [q, r];
}

/** Axial coordinates of the hex containing map point (x, y). */
export function pointToAxial(lat: Lattice, x: number, y: number): [number, number] {
  const rf = (y - lat.originY) / (1.5 * lat.hexR);
  const qf = (x - lat.originX) / (SQRT3 * lat.hexR) - rf / 2;
  return axialRound(qf, rf);
}

/** Lattice cell containing (x, y), or −1 if outside the lattice. */
export function pointToCell(lat: Lattice, x: number, y: number): number {
  const [q, r] = pointToAxial(lat, x, y);
  return axialToCell(lat, q, r);
}

export function hexDistance(q1: number, r1: number, q2: number, r2: number): number {
  const dq = q1 - q2;
  const dr = r1 - r2;
  return (Math.abs(dq) + Math.abs(dr) + Math.abs(dq + dr)) / 2;
}
